/**
 * Utility functions for handling Udemy course links with automatic coupon code application
 */

interface PromotionalConfig {
  isEnabled: boolean;
  couponCode: string;
  startDate: Date;
  endDate: Date;
}

/**
 * Checks if the promotional campaign is currently active
 * @param config - The promotional configuration
 * @returns boolean indicating if promotion is active
 */
export const isPromotionActive = (config: PromotionalConfig): boolean => {
  if (!config.isEnabled) return false;
  const now = new Date();
  return now >= config.startDate && now <= config.endDate;
};

/**
 * Appends coupon code to Udemy URL if promotion is active
 * @param baseUrl - The base Udemy course URL
 * @param config - The promotional configuration  
 * @param isActive - Whether the promotion is currently active
 * @returns The URL with coupon code appended if applicable
 */
export const buildUdemyUrl = (baseUrl: string, config: PromotionalConfig, isActive: boolean): string => {
  if (!baseUrl || !isActive || !config.couponCode) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    url.searchParams.set('couponCode', config.couponCode);
    return url.toString();
  } catch (error) {
    // If URL parsing fails, return original URL
    console.warn('Failed to parse Udemy URL:', baseUrl, error);
    return baseUrl;
  }
};

/**
 * Builds a complete Udemy course URL with automatic coupon code application
 * @param courseSlug - The Udemy course slug (e.g., 'generative-ai-in-software-automation-testing')
 * @param config - The promotional configuration
 * @param isActive - Whether the promotion is currently active
 * @returns Complete Udemy URL with coupon code if applicable
 */
export const buildUdemyCourseUrl = (courseSlug: string, config: PromotionalConfig, isActive: boolean): string => {
  const baseUrl = `https://www.udemy.com/course/${courseSlug}/`;
  return buildUdemyUrl(baseUrl, config, isActive);
};

/**
 * Hook-style function to get Udemy URL builder with current promotional context
 * This can be used in components that have access to promotional context
 */
export const createUdemyUrlBuilder = (config: PromotionalConfig, isActive: boolean) => {
  return {
    buildUrl: (baseUrl: string) => buildUdemyUrl(baseUrl, config, isActive),
    buildCourseUrl: (courseSlug: string) => buildUdemyCourseUrl(courseSlug, config, isActive),
    isActive
  };
};